import { ComponentType, lazy, LazyExoticComponent } from 'react';
import { loadRemote, registerRemotes } from '@module-federation/runtime';
import { HostContext, RemoteManifestEntry } from 'mfe/types';

type RemoteComponentProps = { context: HostContext };

interface LoadedRemote extends RemoteManifestEntry {
  Component: LazyExoticComponent<ComponentType<RemoteComponentProps>>;
}

let cached: Promise<LoadedRemote[]> | undefined;

// Le manifeste est hébergé hors du bundle (blob Azure, une URL par environnement)
// pour qu'ajouter ou retirer un remote n'exige pas de redéployer le host. Sans URL
// configurée — le cas en local — on retombe sur le fichier livré dans public/.
// `||` et non `??` : une variable définie mais vide doit aussi déclencher le repli.
function manifestUrl(): string {
  return import.meta.env.VITE_MFE_MANIFEST_URL || `${import.meta.env.BASE_URL}mfe/manifest.json`;
}

async function fetchAndRegister(): Promise<LoadedRemote[]> {
  const url = manifestUrl();
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Manifeste ${url} : HTTP ${response.status}`);
  }
  const entries: unknown = await response.json();
  if (!Array.isArray(entries)) {
    throw new Error(`Manifeste ${url} : un tableau est attendu`);
  }
  const remotes = entries as RemoteManifestEntry[];

  // Un remote peut exposer plusieurs modules (plusieurs entrées de manifeste,
  // même name/entry) : ne l'enregistrer qu'une fois auprès du runtime MF.
  const uniqueRemotes = [...new Map(remotes.map(r => [r.name, r])).values()];
  registerRemotes(uniqueRemotes.map(({ name, entry }) => ({ name, entry, type: 'module' })));

  return remotes.map(remote => ({
    ...remote,
    Component: lazy(async () => {
      const module = await loadRemote<{ default: ComponentType<RemoteComponentProps> }>(
        `${remote.name}/${remote.module.replace(/^\.\//, '')}`,
      );
      if (!module?.default) {
        throw new Error(`Remote ${remote.name} : le module ${remote.module} doit exposer un default export`);
      }
      return { default: module.default };
    }),
  }));
}

// Memoise au niveau module : idempotent sous StrictMode (double-mount des effets).
function loadRemotes(): Promise<LoadedRemote[]> {
  cached ??= fetchAndRegister();
  return cached;
}

export { loadRemotes };
export type { LoadedRemote };
